export type Path = string[];

export type PathWithValue<ValueType = unknown> = {
  path: Path;
  value: ValueType;
}

export function assignToPath(target: unknown, path: Path, value: unknown, createMissingValues: boolean = false): boolean {
  const pathToParent = Array.from(path);
  const propertyName = pathToParent.pop() ?? '';
  const parent = getValueFromPath(target, pathToParent, createMissingValues);
  if (parent instanceof Object && (propertyName in parent || createMissingValues)) {
    Object.defineProperty(parent, propertyName, { value, enumerable: true, writable: true });
    return true;
  }
  throw new Error(`Cannot access "${path.join('.')}" on ${JSON.stringify(target)}`);
}

// Recursive that iterates on object to get all its path and values. DO NOT USE ON CIRCULAR OBJECTS
export function getPaths(currentObject: unknown, currentPath: Path = []): PathWithValue[] {
  if (typeof currentObject === 'string') {
    return [{ path: currentPath, value: currentObject }];
  }
  if (!(currentObject instanceof Object)) {
    return [];
  }
  return Object.entries(currentObject)
    .map(([key, value]): [string[], unknown] => ([currentPath.concat(key), value]))
    .flatMap(([path, value]) => getPaths(value, path));
}

export function getValueFromPath(obj: unknown, path: Path, createMissingProperties: boolean = false): unknown {
  return path.reduce((value, propertyName) => {
    if (hasProperties(value, propertyName)) {
      return value[propertyName];
    }
    if (createMissingProperties) {
      if (!(value instanceof Object)) {
        throw new Error(`Cannot access property ${path}: ${value} is not an object`);
      }
      const createdValue = {};
      Object.defineProperty(value, propertyName, { value: createdValue, enumerable: true, writable: true });
      return createdValue;
    }
    return {};
  }, obj);
}

export function hasProperties<ObjectType, Properties extends PropertyKey>(
  object: ObjectType,
  ...properties: Properties[]
): object is ObjectType & Record<Properties, string> {
  return object instanceof Object && properties.every(property => property in object);
}
